/**
 * Evidence — what the system may conclude from this attempt, and why.
 *
 * Every claim the analysis made (the verdict, the diagnosis, clean, the first
 * wrong step, the method) with its reliability and the reasons for it. Where a
 * claim is tentative because the reading behind it is in doubt, the reviewer
 * answers one question per doubtful unit — what is written there — and the
 * claims citing it are re-assessed. Settling settles the READING, not the
 * diagnosis; grading the cause is a separate act. The mark never changes here.
 *
 * Approving or rejecting the diagnosis is that separate act: final, logged, the
 * same endpoint a teacher uses (diagnosis-review-target.md). Students with no
 * school have no teacher, so this is the only place their proposed diagnoses
 * are decided.
 *
 * See docs/FeatureDesigns/diagnosis/accuracy/diagnosis-selection-target.md §11.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { fetchWithAdmin } from '../../api/fetchHelper';
import { REASON_TEXT, UNIT_TEXT } from './evidenceText';
import './EvidencePanel.css';

interface Unit {
  unitId: string; agreement: string; confidence?: number; crossedOut?: boolean;
  reviewer?: { verdict: string; text?: string; at: string };
  /** A teacher's or admin's mark on the step (step-marking-target.md). */
  marking?: { verdict: 'right' | 'wrong'; why?: string; text?: string; system?: string; by: string; byRole: string; at: string };
}
interface Claim {
  claimType: string;
  content: Record<string, unknown>;
  cites: { unitIds: string[]; derivedFrom?: string };
  assessment: { reliability: string; reasons: string[]; assessor: string };
  statusHistory?: { status: string; at: string; why: string }[];
  review?: { state: 'approved' | 'rejected'; via?: 'steps'; by: string; byRole: string; at: string; comment?: string };
}
export interface EvidenceView {
  revision: number;
  reading: { attribution?: string; attributionReview?: { verdict: string }; hadImage: boolean; units: Unit[] };
  claims: Claim[];
}

/** What the readers saw, from the analysis — shown beside the doubtful unit. */
interface RegradeResult {
  rule: string;
  applied: boolean;
  changes: Array<{ claimType: string; before: { reliability: string; reasons: string[] }; after: { reliability: string; reasons: string[] } }>;
}

export interface ReaderVersions {
  answer?: { extracted?: string; earlier?: string; served?: string };
  lines?: string[];
}

type Verdict = 'confirmed' | 'misread' | 'unreadable' | 'crossed_out' | 'not_this_question';

const READING_DOUBTS = ['readers_disagree', 'reading_low_confidence', 'line_low_confidence', 'question_number_inferred', 'crossed_out'];

function toSettle(ev: EvidenceView): string[] {
  const out = new Set<string>();
  // The same rule as the server's unitsToSettle: the units a tentative
  // diagnosis rests on.
  for (const c of ev.claims) {
    if (c.claimType !== 'diagnosis' || c.review || c.assessment.reliability !== 'low' || !c.assessment.reasons.some(r => READING_DOUBTS.includes(r))) continue;
    for (const id of c.cites.unitIds) if (!ev.reading.units.find(u => u.unitId === id)?.reviewer) out.add(id);
    if (ev.reading.attribution === 'inferred' && !ev.reading.attributionReview) out.add('attribution');
  }
  return [...out];
}

/** The diagnosis's tier, as the server's `tierOf` (selection/review.ts). */
function tierOf(c: Claim): 'use' | 'proposed' | 'withheld' | 'rejected' {
  if (c.review?.state === 'approved') return 'use';
  if (c.review?.state === 'rejected') return 'rejected';
  const r = c.assessment.reliability;
  return r === 'high' ? 'use' : r === 'none' ? 'withheld' : 'proposed';
}

const TIER_TEXT = {
  use: 'used — shown to the student and counted',
  proposed: 'proposed — not shown to the student or counted until someone approves it',
  withheld: 'withheld — not shown, not asked (approving is refused; re-evaluate instead)',
  rejected: 'rejected — used nowhere',
};

const CHIP: Record<string, string> = { high: 'ev-high', medium: 'ev-medium', low: 'ev-low', none: 'ev-none' };

export function EvidencePanel({ submissionId, evidence, readers, onSettled, onReEvaluate }: {
  submissionId: string;
  evidence?: EvidenceView;
  readers: ReaderVersions;
  onSettled: () => void;
  onReEvaluate: () => void;
}) {
  const [text, setText] = useState<Record<string, string>>({});
  const [misreadVerdict, setMisreadVerdict] = useState(false);
  const [reviewComment, setReviewComment] = useState('');
  const review = useMutation({
    mutationFn: (decision: 'approve' | 'reject') =>
      fetchWithAdmin<{ momentsChanged: number }>(`/solution-analyses/${submissionId}/diagnosis/review`, {
        method: 'POST',
        body: JSON.stringify({ decision, ...(reviewComment.trim() ? { comment: reviewComment.trim() } : {}) }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => { setReviewComment(''); onSettled(); },
  });
  const settle = useMutation({
    mutationFn: (body: { unitId: string; verdict: Verdict; text?: string }) =>
      fetchWithAdmin<{ verdictRestedOnMisread: boolean }>(`/console/solution-analyses/${submissionId}/evidence/settle`, {
        method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: r => { setMisreadVerdict(r.verdictRestedOnMisread); onSettled(); },
  });
  // Re-apply the current rules to the stored grades — no model call. Preview
  // first; Apply writes and records an adjustment (regradeEvidence.ts).
  const [regradePreview, setRegradePreview] = useState<RegradeResult | null>(null);
  const regrade = useMutation({
    mutationFn: (preview: boolean) =>
      fetchWithAdmin<RegradeResult>(`/console/solution-analyses/${submissionId}/evidence/regrade`, {
        method: 'POST', body: JSON.stringify({ preview }), headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: (r, preview) => {
      if (preview) setRegradePreview(r);
      else { setRegradePreview(null); onSettled(); }
    },
  });

  if (!evidence) {
    return (
      <section className="ev-panel">
        <h3>Evidence</h3>
        <p className="ev-muted">No evidence on this attempt — it was analysed before evidence was recorded, and history has not been given it yet.</p>
      </section>
    );
  }

  const pending = toSettle(evidence);
  const marks = evidence.reading.units.filter(u => u.marking);
  const diagnosis = evidence.claims.find(c => c.claimType === 'diagnosis');
  const shownFor = (unitId: string) => {
    if (unitId === 'answer') {
      const a = readers.answer;
      return a ? [a.served && `analysis: ${a.served}`, a.extracted && `extract-visual: ${a.extracted}`, a.earlier && `splitter: ${a.earlier}`].filter(Boolean).join(' · ') : '';
    }
    if (unitId === 'attribution') return 'Is the work shown this question’s?';
    const i = Number(unitId.slice(1)) - 1;
    const u = evidence.reading.units.find(x => x.unitId === unitId);
    return [readers.lines?.[i] && `analysis read: ${readers.lines[i]}`, typeof u?.confidence === 'number' && `confidence ${u.confidence}`].filter(Boolean).join(' · ');
  };
  const send = (unitId: string, verdict: Verdict) => settle.mutate({ unitId, verdict, ...(text[unitId] ? { text: text[unitId] } : {}) });

  return (
    <section className="ev-panel">
      <h3>Evidence</h3>
      <table className="ev-claims">
        <thead><tr><th>claim</th><th>rests on</th><th>reliability</th><th>why</th></tr></thead>
        <tbody>
          {evidence.claims.map((c, i) => (
            <tr key={i}>
              <td>{c.claimType}{typeof c.content.cause === 'string' ? <div className="ev-muted">{c.content.cause}</div> : null}</td>
              <td>{c.cites.unitIds.map(UNIT_TEXT).join(', ')}{c.cites.derivedFrom ? <span className="ev-muted"> ({c.cites.derivedFrom})</span> : null}</td>
              <td><span className={`ev-chip ${CHIP[c.assessment.reliability] ?? ''}`}>{c.assessment.reliability}</span> <span className="ev-muted">{c.assessment.assessor}</span></td>
              <td>{c.assessment.reasons.map(r => REASON_TEXT[r] ?? r).join('; ') || '—'}
                {c.statusHistory?.length ? <div className="ev-muted">{c.statusHistory[c.statusHistory.length - 1].why}</div> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="ev-settle">
        <h4>Re-grade under current rules <span className="ev-muted">— no model call</span></h4>
        {!regradePreview && (
          <button disabled={regrade.isPending} onClick={() => regrade.mutate(true)}>Preview re-grade</button>
        )}
        {regradePreview && regradePreview.changes.length === 0 && (
          <p className="ev-muted">Nothing changes under {regradePreview.rule}.</p>
        )}
        {regradePreview && regradePreview.changes.length > 0 && (
          <>
            <p className="ev-muted">Under {regradePreview.rule}:</p>
            <ul>
              {regradePreview.changes.map((c, i) => (
                <li key={i}>
                  <strong>{c.claimType}</strong>: {c.before.reliability} → {c.after.reliability}
                  <div className="ev-muted">
                    {c.before.reasons.map(r => REASON_TEXT[r] ?? r).join('; ') || '—'} → {c.after.reasons.map(r => REASON_TEXT[r] ?? r).join('; ') || '—'}
                  </div>
                </li>
              ))}
            </ul>
            <button disabled={regrade.isPending} onClick={() => regrade.mutate(false)}>Apply re-grade</button>{' '}
            <button disabled={regrade.isPending} onClick={() => setRegradePreview(null)}>Cancel</button>
          </>
        )}
        {regrade.isError && <p className="ev-error">{(regrade.error as Error).message}</p>}
      </div>

      {diagnosis && (
        <div className="ev-settle">
          <h4>Diagnosis review</h4>
          <p>
            <strong>{tierOf(diagnosis)}</strong> <span className="ev-muted">— {TIER_TEXT[tierOf(diagnosis)]}</span>
            {diagnosis.review && (
              <span className="ev-muted">
                {' '}· {diagnosis.review.state}{diagnosis.review.via === 'steps' ? ' by step marks of' : ' by'} {diagnosis.review.byRole} {diagnosis.review.by}, {new Date(diagnosis.review.at).toLocaleString()}
                {diagnosis.review.comment ? ` — “${diagnosis.review.comment}”` : ''}
              </span>
            )}
          </p>
          <div className="ev-actions">
            {tierOf(diagnosis) !== 'withheld' && !(diagnosis.review?.state === 'approved') && (
              <button disabled={review.isPending} onClick={() => review.mutate('approve')}>Approve</button>
            )}
            {diagnosis.review?.state !== 'rejected' && (
              <button disabled={review.isPending} onClick={() => review.mutate('reject')}>Reject</button>
            )}
            <input placeholder="comment (optional)" value={reviewComment} onChange={e => setReviewComment(e.target.value)} />
          </div>
          {review.isError && <p className="ev-error">{(review.error as Error).message}</p>}
        </div>
      )}

      {marks.length > 0 && (
        <div className="ev-settle">
          <h4>Step marks</h4>
          <ul className="ev-marks">
            {marks.map(u => (
              <li key={u.unitId}>
                <strong>{UNIT_TEXT(u.unitId)}</strong> marked <strong>{u.marking!.verdict}</strong>
                {u.marking!.system ? <span className="ev-muted"> (analysis: {u.marking!.system})</span> : null}
                {u.marking!.why ? <> — {u.marking!.why === 'misread' ? 'we misread it' : 'we judged it wrong'}</> : null}
                {u.marking!.text ? <> — reads “{u.marking!.text}”</> : null}
                <span className="ev-muted"> · {u.marking!.byRole} {u.marking!.by}, {new Date(u.marking!.at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pending.length > 0 && (
        <div className="ev-settle">
          <h4>Settle the reading</h4>
          <p className="ev-muted">Look at the page above and say what is written. This decides whether the claims resting on it are used — not whether the diagnosis is right.</p>
          {pending.map(unitId => (
            <div key={unitId} className="ev-unit">
              <div><strong>{UNIT_TEXT(unitId)}</strong> <span className="ev-muted">{shownFor(unitId)}</span></div>
              {unitId === 'attribution' ? (
                <div className="ev-actions">
                  <button disabled={settle.isPending} onClick={() => send(unitId, 'confirmed')}>It is this question’s work</button>
                  <button disabled={settle.isPending} onClick={() => send(unitId, 'not_this_question')}>Not this question’s work</button>
                </div>
              ) : (
                <div className="ev-actions">
                  <button disabled={settle.isPending} onClick={() => send(unitId, 'confirmed')}>Read correctly</button>
                  <input placeholder="what is written (if misread)" value={text[unitId] ?? ''} onChange={e => setText({ ...text, [unitId]: e.target.value })} />
                  <button disabled={settle.isPending} onClick={() => send(unitId, 'misread')}>Misread</button>
                  <button disabled={settle.isPending} onClick={() => send(unitId, 'unreadable')}>Can’t read it either</button>
                  <button disabled={settle.isPending} onClick={() => send(unitId, 'crossed_out')}>Crossed out</button>
                </div>
              )}
            </div>
          ))}
          {settle.isError && <p className="ev-error">{(settle.error as Error).message}</p>}
        </div>
      )}

      {misreadVerdict && (
        <p className="ev-warn">
          The verdict rested on the answer you found misread, so the mark may be wrong.{' '}
          <button onClick={onReEvaluate}>Re-evaluate</button>
        </p>
      )}
    </section>
  );
}
